import React, {useState, useEffect} from 'react';
import {Window, Button, TextInput, Fieldset} from 'react95';
import styles from '../styles/modules/Window.module.css';

import {t} from '../data/translations';
import {wishlistService, WishlistItem} from '../services/wishlistService';
import {authService} from '../services/authService';

interface WindowWishlistProps {
  onClose: () => void;
}

const WindowWishlist: React.FC<WindowWishlistProps> = ({onClose}) => {
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemLink, setNewItemLink] = useState('');
  const [addingItem, setAddingItem] = useState(false);
  const [currentUser, setCurrentUser] = useState(authService.getCurrentUser());
  const [successMessage, setSuccessMessage] = useState('');
  const [justReservedId, setJustReservedId] = useState<string | null>(null);

  useEffect(() => {
    loadWishlist();
  }, []);

  const loadWishlist = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await wishlistService.getAllItems();
      if (response.success) {
        setItems(response.data as WishlistItem[]);
      } else {
        setError(response.error || 'Ошибка загрузки списка желаний');
      }
    } catch (err) {
      setError('Ошибка загрузки списка желаний');
    } finally {
      setLoading(false);
    }
  };

  const handleReserve = async (item: WishlistItem) => {
    if (!currentUser) {
      setError('Пользователь не авторизован');
      return;
    }

    try {
      const response = await wishlistService.reserveItem(item.id, currentUser.name, currentUser.secretKey);

      if (response.success) {
        setError(''); // Очищаем предыдущие ошибки
        setSuccessMessage(`✅ Подарок "${item.name}" успешно забронирован!`);
        setJustReservedId(item.id);
        setTimeout(() => {
          setSuccessMessage('');
          setJustReservedId(null);
        }, 4000);

        // Обновляем только конкретный элемент, не перезагружаем весь список
        setItems(prevItems =>
          prevItems.map(prevItem =>
            prevItem.id === item.id
              ? {...prevItem, reserved_by: currentUser.name, secret_key: currentUser.secretKey}
              : prevItem
          )
        );
      } else {
        setSuccessMessage(''); // Очищаем сообщение об успехе
        setError(response.error || 'Ошибка бронирования');
      }
    } catch (err) {
      setError('Ошибка бронирования');
    }
  };

  const handleCancelReservation = async (item: WishlistItem) => {
    if (!currentUser) {
      setError('Пользователь не авторизован');
      return;
    }

    try {
      const response = await wishlistService.cancelReservation(item.id, currentUser.secretKey);

      if (response.success) {
        setError(''); // Очищаем предыдущие ошибки
        setSuccessMessage(`🔓 Бронь с подарка "${item.name}" успешно снята!`);
        setJustReservedId(item.id);
        setTimeout(() => {
          setSuccessMessage('');
          setJustReservedId(null);
        }, 4000);

        // Обновляем только конкретный элемент, не перезагружаем весь список
        setItems(prevItems =>
          prevItems.map(prevItem =>
            prevItem.id === item.id ? {...prevItem, reserved_by: undefined, secret_key: undefined} : prevItem
          )
        );
      } else {
        setSuccessMessage(''); // Очищаем сообщение об успехе
        setError(response.error || 'Ошибка отмены брони');
      }
    } catch (err) {
      setError('Ошибка отмены брони');
    }
  };

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemName.trim()) return;

    setAddingItem(true);

    try {
      const response = await wishlistService.addItem(newItemName.trim(), newItemLink.trim() || undefined);

      if (response.success) {
        setNewItemName('');
        setNewItemLink('');
        setShowAddForm(false);
        await loadWishlist();
      } else {
        setSuccessMessage(''); // Очищаем сообщение об успехе
        setError(response.error || 'Ошибка добавления подарка');
      }
    } catch (err) {
      setError('Ошибка добавления подарка');
    } finally {
      setAddingItem(false);
    }
  };

  const isReservedByCurrentUser = (item: WishlistItem) => {
    return currentUser && item.secret_key === currentUser.secretKey;
  };

  const isAvailable = (item: WishlistItem) => {
    return !item.reserved_by;
  };

  return (
    <Window className={`${styles.window} ${styles.wishlistWindow}`}>
      <div className={styles.windowHeader}>
        <span>{t('windows.wishlist.title')}</span>
        <Button onClick={onClose} size="sm">
          <span>✕</span>
        </Button>
      </div>
      <div className={styles.windowContent}>
        <div className={styles.wishlistContainer}>
          <h1 className={styles.wishlistTitle}>{t('windows.wishlist.mainTitle')}</h1>

          {error && <div className={styles.errorMessage}>{error}</div>}
          {successMessage && <div className={styles.successMessage}>{successMessage}</div>}

          {loading ? (
            <div className={styles.loading}>Загрузка списка желаний...</div>
          ) : items.length === 0 ? (
            <div className={styles.emptyState}>
              <p>{t('windows.wishlist.noGifts')}</p>
            </div>
          ) : (
            <div className={styles.wishlistItems}>
              {items.map(item => (
                <div
                  key={item.id}
                  className={`${styles.wishlistItem} ${justReservedId === item.id ? styles.justReserved : ''}`}
                >
                  <div className={styles.itemInfo}>
                    <div className={styles.itemName}>{item.name}</div>
                    {item.link && (
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className={styles.itemLink}>
                        🔗 Ссылка
                      </a>
                    )}
                    {item.reserved_by ? (
                      <div
                        className={`${styles.itemStatus} ${
                          isReservedByCurrentUser(item) ? styles.reservedByMe : styles.reservedByOther
                        }`}
                      >
                        {isReservedByCurrentUser(item) ? (
                          <><strong>🎁 ВЫ ЗАБРОНИРОВАЛИ ЭТОТ ПОДАРОК</strong></>
                        ) : (
                          <><strong>🔒 ПОДАРОК УЖЕ ЗАБРОНИРОВАН</strong></>
                        )}
                      </div>
                    ) : (
                      <div className={`${styles.itemStatus} ${styles.available}`}>✅ Доступно для бронирования</div>
                    )}
                  </div>
                  <div className={styles.itemActions}>
                    {isReservedByCurrentUser(item) ? (
                      <Button
                        onClick={() => handleCancelReservation(item)}
                        size="sm"
                        className={styles.cancelButton}
                      >
                        {t('windows.wishlist.cancelReservation')}
                      </Button>
                    ) : isAvailable(item) ? (
                      <Button onClick={() => handleReserve(item)} size="sm" className={styles.reserveButton}>
                        {t('windows.wishlist.reserveGift')}
                      </Button>
                    ) : (
                      <Button disabled size="sm" className={styles.disabledButton}>
                        🔒 Забронировано
                      </Button>
                    )}
                  </div>
                  {item.reserved_by && !isReservedByCurrentUser(item) && (
                    <div className={styles.reservedOverlay}>
                      <div className={styles.reservedBadge}>
                        <span className={styles.reservedIcon}>🔒</span>
                        <span className={styles.reservedText}>ЗАБРОНИРОВАНО</span>
                      </div>
                    </div>
                  )}
                  {isReservedByCurrentUser(item) && (
                    <div className={styles.myReservedOverlay}>
                      <div className={styles.myReservedBadge}>
                        <span className={styles.myReservedIcon}>🎁</span>
                        <span className={styles.myReservedText}>ВАША БРОНЬ</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Window>
  );
};

export default WindowWishlist;
